import {ImgHistoryItem} from '../../models/editor/imgHistoryItem'
import {EditorStore} from '../../stores/editor/editor.store'
import {AppDispatch} from '../../stores/configureStore'
import {
  IMG_ADDED,
  IMG_ATTRIBUTES_UPDATED,
  IMG_BRIGHTNESS_RESET,
  IMG_MIRRORED_X,
  IMG_MIRRORED_Y,
  IMG_REMOVED,
  IMG_REPOSITIONED,
  IMG_RESCALED,
  IMG_ROTATED,
  IMG_SENT_BACK,
  IMG_SENT_FRONT,
} from '../../models/editor/imgHistoryItemAction'
import {
  addImage,
  bringFront,
  deleteImage,
  mirrorX,
  mirrorY,
  sendBack,
  setAttribute,
  setBrightness,
  setPosition,
  setRotation,
  setScale,
  updateImageHistoryPosition,
} from '../../stores/editor/editor.reducer'

export const isPreviousImageHistoryItemAvailable = (state: EditorStore) =>
  state.imageHistory.length > 0 &&
  (null === state.imageHistoryPosition || state.imageHistoryPosition > 0)
export const isNextImageHistoryItemAvailable = (state: EditorStore) =>
  state.imageHistory.length > 0 &&
  null !== state.imageHistoryPosition &&
  state.imageHistoryPosition < state.imageHistory.length

export const previousImageHistoryItem = (
  dispatch: AppDispatch,
  state: EditorStore,
) => {
  // only undo if there are items and the last position was null or > 0
  if (!isPreviousImageHistoryItemAvailable(state)) {
    return
  }

  // if no position was saved yet, it means that we have to undo the last history item
  // decrement it if there is a position
  const previousImageHistoryPosition =
    (null === state.imageHistoryPosition
      ? state.imageHistory.length
      : state.imageHistoryPosition) - 1

  dispatch(updateImageHistoryPosition({position: previousImageHistoryPosition}))

  undoImageHistoryItem(
    dispatch,
    state,
    state.imageHistory[previousImageHistoryPosition],
  )
}

export const nextImageHistoryItem = (
  dispatch: AppDispatch,
  state: EditorStore,
) => {
  if (!isNextImageHistoryItemAvailable(state)) {
    return
  }

  if (null === state.imageHistoryPosition) {
    return
  }

  // redo the item that was undo previously
  redoImageHistoryItem(
    dispatch,
    state,
    state.imageHistory[state.imageHistoryPosition],
  )

  // increment or clear the position if the previous position was the last item
  const nextImageHistoryPosition =
    state.imageHistoryPosition === state.imageHistory.length - 1
      ? null
      : state.imageHistoryPosition + 1

  dispatch(updateImageHistoryPosition({position: nextImageHistoryPosition}))
}

export const undoImageHistoryItem = (
  dispatch: AppDispatch,
  state: EditorStore,
  imageHistoryItem: ImgHistoryItem,
) => {
  if (IMG_ADDED === imageHistoryItem.action) {
    dispatch(deleteImage({id: imageHistoryItem.img.id}))
  }

  if (IMG_REMOVED === imageHistoryItem.action) {
    dispatch(addImage({image: imageHistoryItem.img}))
  }

  if (IMG_RESCALED === imageHistoryItem.action) {
    if (undefined !== imageHistoryItem.img.origScale) {
      dispatch(
        setScale({
          id: imageHistoryItem.img.id,
          scale: imageHistoryItem.img.origScale,
        }),
      )
    }
  }

  if (IMG_ROTATED === imageHistoryItem.action) {
    if (undefined !== imageHistoryItem.img.origRotation) {
      dispatch(
        setRotation({
          id: imageHistoryItem.img.id,
          rotation: imageHistoryItem.img.origRotation,
        }),
      )
    }
  }

  if (IMG_REPOSITIONED === imageHistoryItem.action) {
    if (imageHistoryItem.img.origPosition) {
      dispatch(
        setPosition({
          id: imageHistoryItem.img.id,
          position: imageHistoryItem.img.origPosition,
        }),
      )
    }
  }

  if (IMG_MIRRORED_X === imageHistoryItem.action) {
    dispatch(
      mirrorX({
        id: imageHistoryItem.img.id,
        mirrorX: !imageHistoryItem.img.mirroredX,
      }),
    )
  }

  if (IMG_MIRRORED_Y === imageHistoryItem.action) {
    dispatch(
      mirrorY({
        id: imageHistoryItem.img.id,
        mirrorY: !imageHistoryItem.img.mirroredY,
      }),
    )
  }

  if (IMG_BRIGHTNESS_RESET === imageHistoryItem.action) {
    if (undefined !== imageHistoryItem.img.origBrightness) {
      dispatch(
        setBrightness({
          id: imageHistoryItem.img.id,
          brightness: imageHistoryItem.img.origBrightness,
        }),
      )
    }
  }

  if (IMG_ATTRIBUTES_UPDATED === imageHistoryItem.action) {
    if (imageHistoryItem.origAttributes) {
      dispatch(
        setAttribute({
          id: imageHistoryItem.img.id,
          data: {...imageHistoryItem.origAttributes},
        }),
      )
    }
  }

  if (IMG_SENT_BACK === imageHistoryItem.action) {
    dispatch(bringFront({id: imageHistoryItem.img.id}))
  }

  if (IMG_SENT_FRONT === imageHistoryItem.action) {
    dispatch(sendBack({id: imageHistoryItem.img.id}))
  }
}

export const redoImageHistoryItem = (
  dispatch: AppDispatch,
  state: EditorStore,
  imageHistoryItem: ImgHistoryItem,
) => {
  if (IMG_ADDED === imageHistoryItem.action) {
    dispatch(addImage({image: imageHistoryItem.img}))
  }

  if (IMG_RESCALED === imageHistoryItem.action) {
    dispatch(
      setScale({
        id: imageHistoryItem.img.id,
        scale: imageHistoryItem.img.scale,
      }),
    )
  }

  if (IMG_ROTATED === imageHistoryItem.action) {
    dispatch(
      setRotation({
        id: imageHistoryItem.img.id,
        rotation: imageHistoryItem.img.rotation,
      }),
    )
  }

  if (IMG_REMOVED === imageHistoryItem.action) {
    dispatch(deleteImage({id: imageHistoryItem.img.id}))
  }

  if (IMG_REPOSITIONED === imageHistoryItem.action) {
    dispatch(
      setPosition({
        id: imageHistoryItem.img.id,
        position: imageHistoryItem.img.position,
      }),
    )
  }

  if (IMG_MIRRORED_X === imageHistoryItem.action) {
    dispatch(
      mirrorX({
        id: imageHistoryItem.img.id,
        mirrorX: imageHistoryItem.img.mirroredX,
      }),
    )
  }

  if (IMG_MIRRORED_Y === imageHistoryItem.action) {
    dispatch(
      mirrorY({
        id: imageHistoryItem.img.id,
        mirrorY: imageHistoryItem.img.mirroredY,
      }),
    )
  }

  if (IMG_BRIGHTNESS_RESET === imageHistoryItem.action) {
    dispatch(
      setBrightness({
        id: imageHistoryItem.img.id,
        brightness: imageHistoryItem.img.brightness,
      }),
    )
  }

  if (IMG_ATTRIBUTES_UPDATED === imageHistoryItem.action) {
    if (imageHistoryItem.newAttributes) {
      dispatch(
        setAttribute({
          id: imageHistoryItem.img.id,
          data: {...imageHistoryItem.newAttributes},
        }),
      )
    }
  }

  if (IMG_SENT_BACK === imageHistoryItem.action) {
    dispatch(sendBack({id: imageHistoryItem.img.id}))
  }

  if (IMG_SENT_FRONT === imageHistoryItem.action) {
    dispatch(bringFront({id: imageHistoryItem.img.id}))
  }
}
